import React, { useContext, useEffect, useState } from 'react'

import { Box } from '@mui/material'
import { useSelectRowTable } from 'hooks/use-select-row-table'
import { constructorInvoice } from 'models/Invoice'
import { BillingService } from 'services/BillingService'
import { ChargeContext } from 'templates/Charge/context'

import EmptyState from 'components/EmptyState/EmptyState'
import { Table as TableComponent } from 'components/Table'
import { TableSkeleton } from 'components/Table/TableSkeleton'

import { FormNegativationContext } from '../../../context'
import { convertToRows } from './utils'

const Table: React.FC = () => {
  const {
    selectedCustomer,
    billings,
    billingSelected,
    updateBillingSelected,
    updateBillings
  } = useContext(FormNegativationContext)

  const { updateModal } = useContext(ChargeContext)

  const [loading, setLoading] = useState<boolean>(false)

  const { select, setSelect, resetSelectedFields } = useSelectRowTable({
    useSelected: false
  })

  const handleOpenModal = (uuid: string) => {
    // Sem ChargeContext quando o formulário vem do detalhe da cobrança.
    updateModal?.((prevState) => ({
      ...prevState,
      chargeDetail: true,
      chargeUuid: uuid
    }))
  }

  const getBillings = async () => {
    try {
      setLoading(true)
      resetSelectedFields({
        selected: [],
        useSelect: true
      })
      const response = await BillingService.getBillingsNegativation(
        selectedCustomer?.value
      )
      const formattedBillings = response?.records
        ?.filter(
          (billing: any) =>
            !billing?.negativation ||
            billing?.negativation?.negativation == null
        )
        ?.map((billing: any) =>
          constructorInvoice({
            ...billing,
            payments: billing?.payments || [],
            discounts: billing?.discounts || [],
            notes: billing?.notes || [],
            message_tracking: billing?.message_tracking || [],
            billing_documents: billing?.billing_documents || [],
            credit_card_charges: billing?.credit_card_charges || []
          })
        )
      updateBillings(formattedBillings)
    } catch (err) {
      // Sem isso `billings` ficaria em `null` e o skeleton nunca sairia da
      // tela; com a lista vazia o usuário cai no empty state.
      updateBillings([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (billingSelected) {
      const uuidBilling = billings.find(
        (billing: any) => billing?.uuid === billingSelected?.uuid
      )?.uuid

      setSelect((prevState) => ({
        ...prevState,
        selected: [uuidBilling]
      }))
    }
  }, [])

  useEffect(() => {
    if (billings && select?.selected.length > 0) {
      const uuid = select?.selected[0]
      updateBillingSelected(
        billings.find((billing: any) => billing.uuid === uuid)
      )
    }
  }, [select?.selected])

  useEffect(() => {
    if (selectedCustomer && !billings) getBillings()
  }, [selectedCustomer])

  // Enquanto a busca das cobranças do cliente está em voo (ou prestes a
  // disparar, logo após a troca de cliente) o skeleton segura o lugar da
  // tabela — sem ele a área fica vazia e sem feedback nenhum.
  if (loading || (selectedCustomer && billings === null)) {
    return (
      <TableSkeleton
        columns={5}
        rows={5}
        showSelect
        hidePagination
        cellRenderHint={['text', 'text', 'chip', 'text', 'icon']}
      />
    )
  }

  if (billings === null) {
    return null
  }

  if (billings !== null && billings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Box maxWidth={{ xs: '100%', md: '60%' }} width="100%">
          <EmptyState
            title="Nenhuma cobrança disponível"
            message="Não há cobranças elegíveis para negativação no momento."
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ overflow: 'auto', maxHeight: '26rem' }}>
      <TableComponent
        mobileCards
        header={[
          { id: 'name', label: 'Cliente' },
          { id: 'due_date', label: 'Vencimento' },
          { id: 'status', label: 'Status' },
          { id: 'amount', label: 'Valor' },
          { id: 'actions', label: '' }
        ]}
        rows={convertToRows({
          records: billings,
          actions: { handleOpenModal }
        })}
        hiddePagination
        select={{
          useSelect: true,
          selected: select.selected,
          setSelected: setSelect,
          singleSelect: true
        }}
      />
    </Box>
  )
}

export default Table
